import { apiErrorMessage } from '@agos/api-client'
import { effectiveRoleKey, T, tt } from '@agos/shared'
import { BUTTON_PRIMARY, cn, ConfirmDialog, Spinner, useToast } from '@agos/ui'
import { useMemo, useState } from 'react'

import {
  roleRefusalCode,
  useAssignRole,
  useRoleOptions,
  type RolePerson,
  type RolesOverview,
} from '../../roles/roles-api'
import { displayParentOf, permissionLabel } from '../permission-matrix'
import { optionConsequences, orderRoles, refusalText, roleNameOf } from '../role-model'
import { RoleChip } from '../RoleChip'
import { TabReadFailed } from '../TabReadFailed'

/**
 * A person's role, and what changing it would do - `GET /settings/roles/options/{employeeId}` and
 * `PUT /employees/{id}/role` (§10A.408).
 *
 * **Every option is the server's answer for this person and this reader**: whether it may be given, and if not why
 * (a code - `refusalText` says it); whether it signs them out (a change of base, §10A.406); their open orders; what they
 * gain and lose. The screen offers nothing the server would refuse, and greys the rest with the reason rather than
 * hiding it, because *why can't I give Jana the dispatcher role* is the question a hidden option leaves unanswered.
 *
 * **The move sends the role it read** (`expectedRoleKey`), so somebody else's move in between is a refusal and a
 * reload rather than an overwrite.
 */
export function RolePicker({ person, overview }: { person: RolePerson; overview: RolesOverview }) {
  const { toast } = useToast()
  const options = useRoleOptions(person.id)
  const assign = useAssignRole()
  const [chosen, setChosen] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [stale, setStale] = useState(false)
  const known = useMemo(() => new Set(overview.allPermissions), [overview.allPermissions])
  const serverChecked = useMemo(() => new Set(overview.serverChecked), [overview.serverChecked])

  if (options.isError) {
    return (
      <TabReadFailed what="options" retry={() => void options.refetch()}>
        <T>Nevieme povedať, ktoré role mu môžete dať a čo by to urobilo. Zmena roly je preto vypnutá.</T>
      </TabReadFailed>
    )
  }

  if (options.isLoading || options.data === undefined) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  const current = options.data.current || effectiveRoleKey(person)
  const rank = orderRoles(overview.roles).map((role) => role.key)
  const ordered = [...options.data.options].sort(
    (left, right) =>
      (rank.indexOf(left.key) === -1 ? rank.length : rank.indexOf(left.key)) -
        (rank.indexOf(right.key) === -1 ? rank.length : rank.indexOf(right.key)) ||
      left.key.localeCompare(right.key),
  )
  const pick = ordered.find((option) => option.key === chosen && option.key !== current)
  const baseOf = (key: string) => overview.roles.find((role) => role.key === key)?.baseRole ?? key
  const labelOf = (key: string) => permissionLabel(key, displayParentOf(key, known))
  const consequences =
    pick === undefined
      ? []
      : optionConsequences(pick, {
          personName: person.name,
          fromBase: person.systemRole,
          toBase: baseOf(pick.key),
          serverChecked,
          labelOf,
        })

  const move = () => {
    if (pick === undefined) return
    assign.mutate(
      { id: person.id, data: { roleKey: pick.key, expectedRoleKey: current } },
      {
        onSuccess: () => {
          setConfirming(false)
          setChosen('')
          toast({
            title: `${person.name} ${tt('má teraz rolu')} ${roleNameOf(overview.roles, pick.key)}`,
            description: pick.signsOut ? tt('Na všetkých zariadeniach sa bude musieť znova prihlásiť.') : undefined,
          })
        },
        onError: (error: unknown) => {
          setConfirming(false)
          if (roleRefusalCode(error) === 'ROLE_STALE') {
            setStale(true)
            return
          }
          toast({
            title: tt('Chyba'),
            description: apiErrorMessage(error, tt('Rolu sa nepodarilo prideliť.')),
            variant: 'destructive',
          })
        },
      },
    )
  }

  return (
    <div className="space-y-2" data-role-picker={person.id}>
      {stale && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-2" data-role-picker-stale>
          <p className="text-xs text-amber-500 flex-1">
            {tt('Rolu tohto človeka medzitým niekto zmenil - načítajte ju znova.')}
          </p>
          <button
            type="button"
            onClick={() => {
              setStale(false)
              setChosen('')
              void options.refetch()
            }}
            className="px-2.5 py-1 rounded-lg border border-border text-xs font-semibold"
          >
            {tt('Načítať znova')}
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {ordered.map((option) => {
          const isCurrent = option.key === current
          const isChosen = option.key === chosen && !isCurrent
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setChosen(option.key)}
              disabled={!option.allowed || isCurrent}
              aria-pressed={isChosen || isCurrent}
              title={!option.allowed ? refusalText(option.code) : undefined}
              data-role-option={option.key}
              data-role-option-allowed={option.allowed}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-xl border px-2.5 py-1.5 text-left transition-colors',
                isChosen ? 'border-primary bg-primary/10' : 'border-border',
                !option.allowed && 'opacity-50',
                option.allowed && !isCurrent && 'hover:bg-muted/50',
              )}
            >
              <RoleChip roleKey={option.key} roles={overview.roles} />
              {isCurrent && <span className="text-[10px] text-muted-foreground">{tt('súčasná rola')}</span>}
              {!option.allowed && (
                <span className="text-[10px] text-muted-foreground" data-role-option-refused={option.code ?? ''}>
                  {refusalText(option.code)}
                </span>
              )}
              {option.allowed && !isCurrent && option.signsOut && (
                <span className="text-[10px] text-amber-500">{tt('odhlási ho')}</span>
              )}
            </button>
          )
        })}
      </div>

      {pick !== undefined && (
        <div className="space-y-1.5 rounded-xl border border-border p-3" data-role-consequences={pick.key}>
          {consequences.length === 0 ? (
            <p className="text-xs text-muted-foreground">{tt('Jeho práva sa nezmenia a nemusí sa znova prihlásiť.')}</p>
          ) : (
            <ul className="space-y-1">
              {consequences.map((consequence) => (
                <li
                  key={consequence.kind}
                  className={cn(
                    'text-xs',
                    consequence.kind === 'signs-out' || consequence.kind === 'server-stops'
                      ? 'text-amber-500'
                      : 'text-muted-foreground',
                  )}
                  data-role-consequence={consequence.kind}
                >
                  {consequence.text}
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setChosen('')}
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
            >
              {tt('Zahodiť')}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={assign.isPending || stale}
              className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
              data-role-assign
            >
              {tt('Zmeniť rolu')}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirming && pick !== undefined}
        onClose={() => setConfirming(false)}
        onConfirm={move}
        title={`${tt('Zmeniť rolu')} ${person.name} ${tt('na')} ${pick === undefined ? '' : roleNameOf(overview.roles, pick.key)}?`}
        description={
          pick?.signsOut === true
            ? `${tt('Zmena roly odhlási')} ${person.name} ${tt('na všetkých zariadeniach.')}`
            : tt('Nové práva platia od jeho ďalšej požiadavky, znova sa prihlasovať nemusí.')
        }
        confirmLabel={tt('Zmeniť rolu')}
        loading={assign.isPending}
      />
    </div>
  )
}
